import React, { useState } from 'react'
import FlexView from 'react-flexview/lib'
import classNames from 'classnames'
import { Divider, FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Select, useMediaQuery } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { KPretty } from '../../../services/utils'
import { OptionalTag } from '../OptionalTag'
import { SkyInformationPopover } from '../../Core/Popover/SkyPopover'
import { RectangleFrame } from '../Frames/RectangleFrame'
import { reportGTM } from '../../Core/Utilities/GTagManger'
import { DiscountTag, LimitedDiscountTag } from '../DiscountTag'
import { ReactComponent as PurpleRibbonSelectDiscountSvg } from '../../../assets/purple_ribbon_select_discount.svg'
import { ReactComponent as YellowRibbonSelectDiscountSvg } from '../../../assets/green_ribbon_select_discount.svg'
import './FormComponents.scss'
import { BillingPeriod } from '../../../models/insurance-models/InsuranceBillingPeriodModel'

const useStyles = makeStyles({
  root: {
    paddingLeft: '14px',
    paddingTop: '14px',
    paddingBottom: '14px',
    position: 'relative',
  },
})

export const Section = ({
  title,
  subtitle,
  content,
  error,
  optional,
  warnings,
  tooltip = undefined,
  shouldHaveDivider,
  removeDeductible,
  isPolicyManagment = false,
  useDivToMinimize = true,
  Popover = SkyInformationPopover,
}) => {
  const isMobile = !useMediaQuery(theme => theme.breakpoints.up('md'))

  return (
    <>
      <Grid container alignItems={'center'} justifyContent={'space-between'} spacing={1}>
        <Grid item sm={12} md={4} lg={5}>
          <FlexView vAlignContent={'top'}>
            <div className={classNames('text-body black margin-right-6', { bolder: !isPolicyManagment })}>
              {title} {removeDeductible && 'Deductible'}
            </div>
            {tooltip && (
              <Popover style={{ fontSize: '0.9rem' }}>
                <div className="text-small">{tooltip}</div>
              </Popover>
            )}
            {optional && <OptionalTag />}
          </FlexView>
          {/* <div className='text-small gray margin-top-small'>{subtitle}</div> */}
        </Grid>
        <Grid item sm={12} md={8} lg={7}>
          {!isMobile ? (
            <FlexView className="space-between">
              {useDivToMinimize ? (
                <>
                  <div /> <div>{content}</div>
                </>
              ) : (
                content
              )}
            </FlexView>
          ) : (
            content
          )}
        </Grid>
      </Grid>
      {error && <div className="text-body margin-top-6 red">{error}</div>}
      {warnings && (
        <div className="margin-right-6">
          <RectangleFrame>{warnings}</RectangleFrame>
        </div>
      )}
      {shouldHaveDivider && <Divider />}
    </>
  )
}

export const SectionNew = ({ content, error, warnings }) => {
  const isMobile = !useMediaQuery(theme => theme.breakpoints.up('md'))

  return (
    <>
      {!isMobile ? <FlexView className="space-between">{content}</FlexView> : content}
      {error && <div className="text-body margin-top-6 red">{error}</div>}
      {warnings && (
        <div className="margin-right-6">
          <RectangleFrame>{warnings}</RectangleFrame>
        </div>
      )}
    </>
  )
}

export const SectionRadioButton = ({ name, value, buttons, errors = {}, onChange, ...props }) => {
  return (
    <>
      <Section
        error={errors[name]}
        {...props}
        content={
          <>
            <RadioGroup value={value} onChange={e => onChange(name, e.target.value)}>
              <FlexView>
                {buttons.map((btn, index) => (
                  <FormControlLabel key={index} label={btn.label} value={btn.value} control={<Radio color="primary" />} />
                ))}
              </FlexView>
            </RadioGroup>
          </>
        }
      />
    </>
  )
}

export const SectionSelect = ({
  name,
  value,
  buttons,
  errors = {},
  onChange,
  removeDeductible,
  shouldHaveDivider = true,
  GTMname,
  discountTag,
  ...props
}) => {
  const reportSelect = () => reportGTM('', '', { event: `${GTMname}-edit` })

  const classes = useStyles()

  return (
    <>
      <Section
        shouldHaveDivider={shouldHaveDivider}
        removeDeductible={removeDeductible}
        error={errors[name]}
        {...props}
        content={
          <Select
            classes={{ root: classes.root }}
            fullWidth
            variant="outlined"
            value={value}
            onChange={e => onChange && onChange(name, e.target.value)}
            onOpen={reportSelect}
          >
            {buttons.map((btn, index) => (
              <MenuItem key={index} value={btn.value}>
                <div className="text-body black">{removeDeductible ? btn.label.replace('Deductible', '') : btn.label}</div>
              </MenuItem>
            ))}
          </Select>
        }
      />
    </>
  )
}

export const SectionSelectWithRibbon = ({
  name,
  value,
  buttons,
  errors = {},
  onChange,
  removeDeductible,
  shouldHaveDivider = true,
  GTMname,
  discountTag,
  discounts,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const reportSelect = () => reportGTM('', '', { event: `${GTMname}-edit` })

  const onOpen = () => {
    reportSelect()
    setIsOpen(true)
  }

  const classes = useStyles()

  return (
    <>
      <SectionNew
        error={errors[name]}
        {...props}
        content={
          <div className="recurrence-select-cont">
            {!isOpen && discounts && BillingPeriod.MONTH != value && (
              <div className="ribbon-cont">
                <div style={{ position: 'relative' }}>
                  <div className={classNames('ribbon-text text-tiny bold')}>
                    <FlexView column hAlignContent="center" vAlignContent="center">
                      <span>${discounts[value]}</span>
                      <span>off</span>
                    </FlexView>
                  </div>
                  {BillingPeriod.ANNUAL == value ? <YellowRibbonSelectDiscountSvg /> : <PurpleRibbonSelectDiscountSvg />}
                </div>
              </div>
            )}
            <Select
              classes={{ root: classes.root }}
              fullWidth
              variant="outlined"
              value={value}
              onChange={e => onChange && onChange(name, e.target.value)}
              onOpen={onOpen}
              onClose={() => setIsOpen(false)}
            >
              {buttons.map((btn, index) => (
                <MenuItem className="menu-item-cont" key={index} value={btn.value}>
                  <Grid container wrap="nowrap" alignItems="center" justifyContent="space-between">
                    <div className="text-body black">{btn.label == 'Annual' ? 'Annually' : btn.label}</div>
                    {discounts && discounts[btn.value] && isOpen && btn.value != BillingPeriod.MONTH && (
                      <LimitedDiscountTag discountPrice={discounts[btn.value]} value={btn.value} />
                    )}
                  </Grid>
                </MenuItem>
              ))}
            </Select>
          </div>
        }
      />
    </>
  )
}

export const SectionSpecialSelect = ({
  name,
  value,
  buttons,
  errors = {},
  warnings,
  onChange,
  shouldHaveDivider = true,
  GTMname,
  ...props
}) => {
  const classes = useStyles()
  const reportSelect = () => reportGTM('', '', { event: `${GTMname}-edit` })

  return (
    <Section
      shouldHaveDivider={shouldHaveDivider}
      error={errors[name]}
      warnings={warnings}
      {...props}
      content={
        <Select
          classes={{ root: classes.root }}
          fullWidth
          variant="outlined"
          value={value}
          onChange={e => onChange && onChange(name, e.target.value)}
          onOpen={reportSelect}
        >
          {buttons.map((btn, index) => (
            <MenuItem key={index} value={btn.value}>
              <FlexView>
                {btn.labels ? (
                  btn.labels.map((label, eIndex) => (
                    <div className="margin-right-small margin-left-small" key={`${index}${eIndex}`}>
                      <div className="text-body gray">{label}</div>
                      <div className="text-body black bold textcenter">{btn.values[eIndex]}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-body black">
                    {btn.label} {btn.name.includes('ALTERNATIVE') ? 'ALTERNATIVE' : ''}
                  </div>
                )}
              </FlexView>
            </MenuItem>
          ))}
        </Select>
      }
    />
  )
}
